import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, MapPin, Users, Clock, DollarSign, ArrowLeft, Trophy, BookOpen, Users2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date without timezone issues
const formatEventDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

const eventTypeConfig = {
  show: { icon: Trophy, label: 'Horse Show', color: 'bg-amber-100 text-amber-800' },
  clinic: { icon: BookOpen, label: 'Clinic', color: 'bg-blue-100 text-blue-800' },
  meetup: { icon: Users2, label: 'Meetup', color: 'bg-green-100 text-green-800' },
};

export const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [formData, setFormData] = useState({
    participant_name: '',
    phone_number: '',
    age_category: ''
  });
  // Multiple horses support
  const [horses, setHorses] = useState([{ horse_name: '', selected_classes: [], age_category: '' }]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${eventId}`);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Event not found');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHorseNameChange = (index, value) => {
    const newHorses = [...horses];
    newHorses[index].horse_name = value;
    setHorses(newHorses);
  };

  const handleHorseClassToggle = (horseIndex, className) => {
    const newHorses = [...horses];
    const classes = newHorses[horseIndex].selected_classes;
    if (classes.includes(className)) {
      newHorses[horseIndex].selected_classes = classes.filter(c => c !== className);
    } else {
      newHorses[horseIndex].selected_classes = [...classes, className];
    }
    setHorses(newHorses);
  };

  const handleHorseAgeCategory = (horseIndex, ageCategory) => {
    const newHorses = [...horses];
    newHorses[horseIndex].age_category = ageCategory;
    setHorses(newHorses);
  };

  const addHorse = () => {
    setHorses([...horses, { horse_name: '', selected_classes: [], age_category: '' }]);
  };

  const removeHorse = (index) => {
    if (horses.length > 1) {
      setHorses(horses.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    if (event?.event_type === 'show') {
      const pricePerClass = event.price || 5;
      const totalClasses = horses.reduce((sum, horse) => sum + horse.selected_classes.length, 0);
      return totalClasses * pricePerClass;
    }
    return event?.price || 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please log in to register');
      navigate('/login');
      return;
    }

    // Validate basic fields
    if (!formData.participant_name || !formData.phone_number) {
      toast.error('Please fill in participant name and phone number');
      return;
    }

    // Validate horses
    const validHorses = horses.filter(h => h.horse_name.trim() !== '');
    if (validHorses.length === 0) {
      toast.error('Please enter at least one horse name');
      return;
    }

    // For horse shows, validate classes
    if (isShow) {
      const hasClasses = validHorses.some(h => h.selected_classes.length > 0);
      if (!hasClasses) {
        toast.error('Please select at least one class for your horse(s)');
        return;
      }
      // Check that each horse with classes has an age category
      const missingAgeCategory = validHorses.find(h => h.selected_classes.length > 0 && !h.age_category);
      if (missingAgeCategory) {
        toast.error(`Please select an age category for ${missingAgeCategory.horse_name || 'each horse'}`);
        return;
      }
    }

    setRegistering(true);
    try {
      // Create registration with multiple horses (each with their own age category)
      const regData = {
        event_id: eventId,
        participant_name: formData.participant_name,
        phone_number: formData.phone_number,
        horses: validHorses.map(h => ({
          horse_name: h.horse_name,
          selected_classes: h.selected_classes,
          age_category: h.age_category || null
        })),
        age_category: validHorses[0]?.age_category || null  // Legacy field
      };

      await axios.post(`${API}/registrations`, regData, { headers: getAuthHeader() });

      toast.success('Registration successful! You can now pay via E-Transfer or Cash.');
      setShowRegistrationModal(false);
      navigate('/my-registrations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  const config = eventTypeConfig[event.event_type] || eventTypeConfig.show;
  const Icon = config.icon;
  const spotsLeft = event.max_participants - event.current_participants;
  const isFull = spotsLeft <= 0;
  const deadlineDate = event.registration_deadline ? new Date(event.registration_deadline) : null;
  const entriesClosed = deadlineDate && deadlineDate < new Date();
  const isShow = event.event_type === 'show';

  return (
    <Layout>
      {/* Hero Banner */}
      <section className="relative h-72 md:h-96 flex items-end">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${event.image_url || 'https://images.unsplash.com/flagged/photo-1568381670226-fab8dc323343?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxob3JzZSUyMHNob3clMjBqdW1waW5nfGVufDB8fHx8MTc2ODY2OTIxM3ww&ixlib=rb-4.1.0&q=85'})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pb-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate('/events')}
            data-testid="back-to-events-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color} flex items-center gap-1`}>
              <Icon className="w-4 h-4" />
              {config.label}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            {event.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-display text-2xl">About This Event</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                    {event.description}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-display text-2xl">Event Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">Date</p>
                      <p className="text-stone-600">
                        {formatEventDate(event.date, { weekday: 'long' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">Time</p>
                      <p className="text-stone-600">{event.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">Location</p>
                      <p className="text-stone-600">{event.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Show Classes & Age Categories */}
              {isShow && event.show_classes && event.show_classes.length > 0 && (
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle className="font-display text-2xl">Available Classes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-stone-600 mb-4">${event.price.toFixed(2)} CAD per class entry</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {event.show_classes.map((className, idx) => (
                        <div 
                          key={idx}
                          className="bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 text-stone-700"
                        >
                          {className}
                        </div>
                      ))}
                    </div>
                    
                    {event.age_categories && event.age_categories.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-medium text-stone-900 mb-3">Age Categories</h4>
                        <div className="flex flex-wrap gap-2">
                          {event.age_categories.map((category, idx) => (
                            <span 
                              key={idx}
                              className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border-stone-200 sticky top-24">
                <CardContent className="p-6 space-y-6">
                  <div className="text-center">
                    <p className="text-stone-500 text-sm">
                      {isShow ? 'Entry Fee' : 'Registration Fee'}
                    </p>
                    <p className="font-mono text-4xl font-bold text-primary">
                      ${event.price}
                    </p>
                    {isShow && (
                      <p className="text-stone-500 text-sm mt-1">CAD per class</p>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2 text-stone-600">
                    <Users className="w-5 h-5" />
                    <span className={spotsLeft <= 5 && spotsLeft > 0 ? 'text-terracotta font-medium' : ''}>
                      {isFull ? 'Event is full' : `${spotsLeft} of ${event.max_participants} spots remaining`}
                    </span>
                  </div>

                  {deadlineDate && !entriesClosed && (
                    <p className="text-xs text-terracotta text-center font-medium" data-testid="entries-close-notice">
                      Entries close {deadlineDate.toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}

                  <div className="border-t border-stone-200 pt-6">
                    {entriesClosed ? (
                      <Button disabled className="w-full" data-testid="entries-closed-btn">
                        Entries Closed
                      </Button>
                    ) : isFull ? (
                      <Button disabled className="w-full" data-testid="register-btn-disabled">
                        Sold Out
                      </Button>
                    ) : user ? (
                      <Button 
                        className="w-full bg-terracotta hover:bg-terracotta-light"
                        onClick={() => setShowRegistrationModal(true)}
                        data-testid="register-btn"
                      >
                        Register Now
                      </Button>
                    ) : (
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => navigate('/login')}
                        data-testid="login-to-register-btn"
                      >
                        Log In to Register
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-stone-500 text-center">
                    Pay via E-Transfer or Cash at Event
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      <Dialog open={showRegistrationModal} onOpenChange={setShowRegistrationModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Register for Event</DialogTitle>
            <DialogDescription>
              Fill in your details to register for {event.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="participant_name">Participant Name *</Label>
              <Input
                id="participant_name"
                name="participant_name"
                placeholder="John Smith"
                value={formData.participant_name}
                onChange={handleChange}
                className="bg-stone-50"
                data-testid="reg-participant-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number *</Label>
              <Input
                id="phone_number"
                name="phone_number"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone_number}
                onChange={handleChange}
                className="bg-stone-50"
                data-testid="reg-phone-number"
              />
            </div>

            {/* Horse Show specific fields */}
            {isShow && (
              <>
                {/* Multiple Horses Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Horses & Classes</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={addHorse}
                      data-testid="add-horse-btn"
                    >
                      + Add Another Horse
                    </Button>
                  </div>
                  
                  {horses.map((horse, horseIndex) => (
                    <Card key={horseIndex} className="border-stone-200">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-medium">Horse {horseIndex + 1}</Label>
                          {horses.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeHorse(horseIndex)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-stone-500">Horse Name *</Label>
                            <Input
                              placeholder="Horse name"
                              value={horse.horse_name}
                              onChange={(e) => handleHorseNameChange(horseIndex, e.target.value)}
                              className="bg-stone-50"
                              data-testid={`horse-name-${horseIndex}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-stone-500">Age Category *</Label>
                            <Select 
                              value={horse.age_category} 
                              onValueChange={(v) => handleHorseAgeCategory(horseIndex, v)}
                            >
                              <SelectTrigger className="bg-stone-50" data-testid={`horse-age-${horseIndex}`}>
                                <SelectValue placeholder="Select age" />
                              </SelectTrigger>
                              <SelectContent>
                                {event.age_categories?.map((category, idx) => (
                                  <SelectItem key={idx} value={category}>{category}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm text-stone-600">Select classes for this horse (${event.price} each)</Label>
                          <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                            {event.show_classes?.map((className, classIdx) => (
                              <div 
                                key={classIdx} 
                                className="flex items-center space-x-2 p-1.5 rounded hover:bg-stone-50"
                              >
                                <Checkbox
                                  id={`horse-${horseIndex}-class-${classIdx}`}
                                  checked={horse.selected_classes.includes(className)}
                                  onCheckedChange={() => handleHorseClassToggle(horseIndex, className)}
                                />
                                <Label 
                                  htmlFor={`horse-${horseIndex}-class-${classIdx}`} 
                                  className="font-normal cursor-pointer text-sm flex-1"
                                >
                                  {className}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {horse.selected_classes.length > 0 && (
                            <p className="text-xs text-primary">
                              {horse.selected_classes.length} class{horse.selected_classes.length !== 1 ? 'es' : ''} = ${horse.selected_classes.length * event.price}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* Non-show events: single horse field */}
            {!isShow && (
              <div className="space-y-2">
                <Label htmlFor="horse_name">Horse Name *</Label>
                <Input
                  id="horse_name"
                  placeholder="Thunder"
                  value={horses[0]?.horse_name || ''}
                  onChange={(e) => handleHorseNameChange(0, e.target.value)}
                  className="bg-stone-50"
                  data-testid="reg-horse-name"
                />
              </div>
            )}

            <div className="bg-stone-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">
                  {isShow 
                    ? `${horses.reduce((sum, h) => sum + h.selected_classes.length, 0)} class entries × $${event.price} CAD` 
                    : 'Registration Fee'}
                </span>
                <span className="font-mono text-xl font-bold text-primary">
                  ${calculateTotal().toFixed(2)} CAD
                </span>
              </div>
              {isShow && horses.length > 1 && (
                <p className="text-xs text-stone-500 mt-2">
                  {horses.filter(h => h.horse_name && h.selected_classes.length > 0).length} horse(s) registered
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-terracotta hover:bg-terracotta-light"
              disabled={registering || (isShow && horses.reduce((sum, h) => sum + h.selected_classes.length, 0) === 0)}
              data-testid="reg-submit-btn"
            >
              {registering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EventDetailPage;
